import axios from "axios";
import { appConfig } from "../../../config";

const REFRESH_PATH = "/authentication/token/refresh/";

const http = axios.create({ baseURL: appConfig.apiBaseUrl });

// axios instance جدا برای refresh (بدون interceptor های http)
const refreshClient = axios.create({ baseURL: appConfig.apiBaseUrl });

// --- token helpers ---
const STORAGE = {
    access: "token",
    refresh: "refreshToken",
    role: "role",
};

const getAccessToken = () => localStorage.getItem(STORAGE.access);
const getRefreshToken = () => localStorage.getItem(STORAGE.refresh);

const setAccessToken = (t) => localStorage.setItem(STORAGE.access, t);

const clearAuth = () => {
    localStorage.removeItem(STORAGE.access);
    localStorage.removeItem(STORAGE.refresh);
    localStorage.removeItem(STORAGE.role);
};

const logoutAndRedirect = () => {
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentUrl !== "/") {
        sessionStorage.setItem("authCallbackUrl", currentUrl);
    }
    clearAuth();
    window.location.assign("/");
};

// --- refresh coordinator ---
let isRefreshing = false;
/** @type {Array<(token: string) => void>} */
let subscribers = [];
/** @type {Array<(err: any) => void>} */
let errorSubscribers = [];

const subscribeTokenRefresh = (cb, errCb) => {
    subscribers.push(cb);
    errorSubscribers.push(errCb);
};

const onRefreshed = (token) => {
    subscribers.forEach((cb) => cb(token));
    subscribers = [];
    errorSubscribers = [];
};

const onRefreshFailed = (err) => {
    errorSubscribers.forEach((cb) => cb(err));
    subscribers = [];
    errorSubscribers = [];
};

async function refreshAccessToken() {
    const rt = getRefreshToken();
    if (!rt) throw new Error("No refresh token");

    const res = await refreshClient.post(REFRESH_PATH, { refresh: rt });

    // با توجه به ساختار بک‌اندت:
    // const newToken = res.data?.data?.access;
    // ولی بهتره fallback هم داشته باشیم:
    const newToken = res.data?.data?.access ?? res.data?.access;

    if (!newToken) throw new Error("Refresh failed: no access token in response");

    setAccessToken(newToken);
    return newToken;
}

// --- request interceptor ---
http.interceptors.request.use(
    (config) => {
        if (config?.skipAuth) return config;

        const token = getAccessToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (err) => Promise.reject(err)
);

// --- response interceptor ---
http.interceptors.response.use(
    (res) => res,
    async (error) => {
        const original = error?.config;

        // اگر config نداریم یا خطا response نداره:
        if (!original) return Promise.reject(error);

        const status = error?.response?.status;

        // Login and other explicitly public requests must surface their own
        // validation errors; they should never enter the token-refresh flow.
        if (original.skipAuth) return Promise.reject(error);

        // اگر خود refresh 401/… خورد، یعنی session مرده
        if (original.url?.includes(REFRESH_PATH)) {
            logoutAndRedirect();
            return Promise.reject(error);
        }

        // فقط برای 401
        if (status !== 401) return Promise.reject(error);

        // جلوگیری از لوپ
        if (original.__isRetryRequest) return Promise.reject(error);
        original.__isRetryRequest = true;

        // اگر refresh در حال انجامه، منتظرش می‌مونیم
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                subscribeTokenRefresh(
                    (token) => {
                        original.headers = original.headers || {};
                        original.headers.Authorization = `Bearer ${token}`;
                        resolve(http(original));
                    },
                    (err) => reject(err)
                );
            });
        }

        isRefreshing = true;

        try {
            const newToken = await refreshAccessToken();
            onRefreshed(newToken);

            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${newToken}`;

            return http(original);
        } catch (e) {
            onRefreshFailed(e);

            // اصولی‌تر: وقتی refresh fail شد، کل auth رو پاک کن
            logoutAndRedirect();

            return Promise.reject(e);
        } finally {
            isRefreshing = false;
        }
    }
);

export default http;
